import { Link, useSearchParams } from 'react-router-dom';
import { LayoutDashboard, Boxes, ClipboardCheck, PackageOpen, History } from 'lucide-react';
import { PageContainer } from '../../../shared/components/PageContainer';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import DashboardAtivosPage from './DashboardAtivosPage';
import AtivosPage from './AtivosPage';
import InventariosPage from './InventariosPage';
import AlmoxarifadoPage from './AlmoxarifadoPage';

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { key: 'lista', label: 'Listar Ativos', icon: Boxes },
  { key: 'inventarios', label: 'Inventário', icon: ClipboardCheck },
  { key: 'almoxarifado', label: 'Almoxarifado', icon: PackageOpen },
] as const;

type TabKey = (typeof TABS)[number]['key'];

/**
 * Tela home do módulo Ativos.
 *
 * Uma única entrada no menu (Compras > Ativos) leva para cá. Quatro abas
 * embutidas — Dashboard (default), Listar Ativos, Inventário e Almoxarifado —
 * mantêm o histórico do navegador via `?tab=` na querystring.
 *
 * Rotas antigas (/ativos/dashboard, /ativos/licencas, /ativos/inventarios)
 * redirecionam pra cá com o `?tab=` correto — ver `AppRouter`. Licenças não
 * têm aba própria: aparecem em "Listar Ativos" (issue #464).
 *
 * A visão global de movimentações (auditoria e export CSV) é página própria,
 * atrás de `ativos.manutencao` — o atalho ao lado das abas só aparece pra quem
 * tem essa permissão, e leva a aba atual no `?origem=` pro breadcrumb voltar
 * pro mesmo lugar.
 */
export default function AtivosHomePage() {
  const [params, setParams] = useSearchParams();
  const { can } = usePermissions();
  const rawTab = params.get('tab');
  const tab: TabKey = (TABS.find((t) => t.key === rawTab)?.key ?? 'dashboard') as TabKey;
  const canSeeMovements = can(FEATURES.ativosManutencao, 'view');

  function selectTab(next: TabKey) {
    setParams((p) => {
      if (next === 'dashboard') p.delete('tab');
      else p.set('tab', next);
      return p;
    });
  }

  return (
    <PageContainer title="Gestão de Ativos">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200">
        <div className="flex gap-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => selectTab(t.key)}
                className={`flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon className="h-4 w-4" />
                {t.label}
              </button>
            );
          })}
        </div>
        {canSeeMovements && (
          <Link
            to={tab === 'dashboard' ? '/ativos/movimentacoes' : `/ativos/movimentacoes?origem=${tab}`}
            className="ml-auto flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <History className="h-4 w-4" />
            Movimentações
          </Link>
        )}
      </div>
      <div className="pt-4">
        {tab === 'dashboard' && <DashboardAtivosPage embedded />}
        {tab === 'lista' && <AtivosPage embedded />}
        {tab === 'inventarios' && <InventariosPage embedded />}
        {tab === 'almoxarifado' && <AlmoxarifadoPage />}
      </div>
    </PageContainer>
  );
}
